/**
 * Line definitions for `TrendChart`.
 *
 * Kept out of the chart module so it exports only a component: colours are
 * design-system classes rather than hex, so a palette change reaches the
 * charts with everything else.
 *
 * The four slots come from the validated categorical ramp (`--color-chart-*`
 * in index.css) and are assigned in a fixed order. They replace an earlier set
 * that paired amber with emerald and used `ink` as a fourth line: the amber
 * and emerald sat below 3:1 against the white card, and `ink` has no chroma at
 * all, so it read as "the axis" rather than as a series. Both are the kind of
 * thing that looks fine to the author and is unreadable to a reader with
 * deuteranopia.
 */

/** The four things a hiring pipeline produces, in the order they happen. */
export const PIPELINE_SERIES = [
  { key: 'applications', label: 'Applications', stroke: 'stroke-chart-1', dot: 'bg-chart-1' },
  { key: 'interviews', label: 'Interviews', stroke: 'stroke-chart-2', dot: 'bg-chart-2' },
  { key: 'offers', label: 'Offers', stroke: 'stroke-chart-3', dot: 'bg-chart-3' },
  { key: 'hires', label: 'Hires', stroke: 'stroke-chart-4', dot: 'bg-chart-4' },
];

/**
 * One listing: who looked, who applied, who was hired.
 *
 * Visitors rather than views: a reload is a view, and a line that jumps
 * because one student refreshed is not telling the company anything.
 */
export const LISTING_SERIES = [
  { key: 'visitors', label: 'Visitors', stroke: 'stroke-chart-1', dot: 'bg-chart-1' },
  { key: 'applications', label: 'Applications', stroke: 'stroke-chart-2', dot: 'bg-chart-2' },
  { key: 'hires', label: 'Hires', stroke: 'stroke-chart-4', dot: 'bg-chart-4' },
];

/** Platform growth: registrations and the supply and demand they create. */
export const GROWTH_SERIES = [
  { key: 'students', label: 'Students', stroke: 'stroke-chart-1', dot: 'bg-chart-1' },
  { key: 'companies', label: 'Companies', stroke: 'stroke-chart-2', dot: 'bg-chart-2' },
  {
    key: 'internships',
    label: 'Internships published',
    stroke: 'stroke-chart-3',
    dot: 'bg-chart-3',
  },
  { key: 'applications', label: 'Applications', stroke: 'stroke-chart-4', dot: 'bg-chart-4' },
];

/** Money: what was billed, what arrived, and what was given away. */
export const REVENUE_SERIES = [
  { key: 'billed_cents', label: 'Billed', stroke: 'stroke-chart-1', dot: 'bg-chart-1' },
  { key: 'collected_cents', label: 'Collected', stroke: 'stroke-chart-2', dot: 'bg-chart-2' },
  { key: 'discounted_cents', label: 'Discounted', stroke: 'stroke-chart-3', dot: 'bg-chart-3' },
];

/**
 * Movement in the paying base.
 *
 * Only four lines: `resumed` is left off the chart and reported as a number
 * instead. It is rare enough that its line sits on the axis and adds a colour
 * a reader has to decode for nothing.
 */
export const SUBSCRIPTION_SERIES = [
  { key: 'started', label: 'Started', stroke: 'stroke-chart-1', dot: 'bg-chart-1' },
  { key: 'upgraded', label: 'Upgraded', stroke: 'stroke-chart-2', dot: 'bg-chart-2' },
  { key: 'downgraded', label: 'Downgraded', stroke: 'stroke-chart-3', dot: 'bg-chart-3' },
  { key: 'cancelled', label: 'Cancelled', stroke: 'stroke-chart-4', dot: 'bg-chart-4' },
];

/**
 * Verification: arrivals against decisions.
 *
 * The point of plotting them together is the gap. Either line alone says
 * whether the team was busy; only both say whether the queue is keeping up.
 */
export const VERIFICATION_SERIES = [
  { key: 'submitted', label: 'Requested', stroke: 'stroke-chart-1', dot: 'bg-chart-1' },
  { key: 'approved', label: 'Approved', stroke: 'stroke-chart-2', dot: 'bg-chart-2' },
  { key: 'rejected', label: 'Rejected', stroke: 'stroke-chart-3', dot: 'bg-chart-3' },
];
